import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import {
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Select,
	SelectContent,
	SelectItem,
	SelectSeparator,
	SelectTrigger,
	SelectValue,
	Textarea,
	Label,
	Badge,
} from "@/components/primitives";
import { formatDate } from "@/lib/utils";
import {
	ArrowLeft,
	Calendar,
	Clock,
	FileText,
	AlertTriangle,
	CheckCircle,
	User,
	Target,
} from "lucide-react";
import {
	getFeelingOptionWithArrows,
	getFeelingText,
} from "@/utils/feeling-utils";

interface DailyCheckIn {
	id: string;
	general_feeling: number;
	day_description?: string;
	reflection_feeling?: number;
	reflection_impact?: string;
	reflection_reasons?: string;
	signals_noticed?: string[];
	corrective_actions_taken?: string[];
	created_at: string;
	reflection_id: string | null;
	reflection?: {
		id: string;
		title: string;
		description: string;
		signals?: {
			id: string;
			title: string;
			description: string;
		}[];
	} | null;
}

export function EditCheckIn() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { user } = useAuth();
	const [checkIn, setCheckIn] = useState<DailyCheckIn | null>(null);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);

	// Form states
	const [editFeeling, setEditFeeling] = useState<number>(1);
	const [editDescription, setEditDescription] = useState("");
	const [editReflectionFeeling, setEditReflectionFeeling] = useState<number>(0);
	const [editReflectionImpact, setEditReflectionImpact] = useState("");
	const [editReflectionReasons, setEditReflectionReasons] = useState("");
	const [editSignalsNoticed, setEditSignalsNoticed] = useState<string[]>([]);

	useEffect(() => {
		if (user && id) {
			fetchCheckIn();
		}
	}, [user, id]);

	const fetchCheckIn = async () => {
		if (!user || !id) return;

		try {
			const { data, error } = await supabase
				.from("daily_checkins")
				.select(
					`
          *,
          reflection:reflections(
            id, 
            title, 
            description,
            signals(id, title, description)
          )
        `,
				)
				.eq("id", id)
				.eq("user_id", user.id)
				.single();

			if (error) throw error;

			setCheckIn(data);

			// Set form values
			setEditFeeling(data.general_feeling);
			setEditDescription(data.day_description || "");
			setEditReflectionFeeling(data.reflection_feeling ?? 0);
			setEditReflectionImpact(data.reflection_impact || "");
			setEditReflectionReasons(data.reflection_reasons || "");
			setEditSignalsNoticed(data.signals_noticed || []);
		} catch (error) {
			console.error("Error fetching check-in:", error);
			navigate("/check-ins");
		} finally {
			setLoading(false);
		}
	};

	const handleSave = async () => {
		if (!checkIn || !user) return;

		setSaving(true);
		try {
			const updateData = {
				general_feeling: editFeeling,
				day_description: editDescription,
				reflection_feeling: editReflectionFeeling,
				reflection_impact: editReflectionImpact,
				reflection_reasons: editReflectionReasons,
				signals_noticed: editSignalsNoticed,
			};

			const { error } = await supabase
				.from("daily_checkins")
				.update(updateData)
				.eq("id", checkIn.id)
				.eq("user_id", user.id);

			if (error) throw error;

			navigate("/check-ins");
		} catch (error) {
			console.error("Error updating check-in:", error);
			alert("Failed to update check-in. Please try again.");
		} finally {
			setSaving(false);
		}
	};

	const getFeelingText = (value: number) => {
		switch (value) {
			case 3:
				return "Much better than my last daily reflection";
			case 1:
				return "Slightly better than my last daily reflection";
			case -1:
				return "Slightly worse than my last daily reflection";
			case -3:
				return "Much worse than my last daily reflection";
			default:
				return "Select how you're feeling";
		}
	};

	const getImpactText = (value: number) => {
		switch (value) {
			case 0:
				return "No Impact";
			case 3:
				return "Break-through Positive";
			case 2:
				return "Highly Positive";
			case 1:
				return "Slightly Positive";
			case -1:
				return "Slightly Negative";
			case -2:
				return "Highly Negative";
			case -3:
				return "Critically Negative";
			default:
				return "Select impact level";
		}
	};

	const handleSignalToggle = (signalId: string) => {
		setEditSignalsNoticed((prev) =>
			prev.includes(signalId)
				? prev.filter((id) => id !== signalId)
				: [...prev, signalId],
		);
	};

	if (loading) {
		return (
			<div className="container mx-auto px-4 py-8">
				<div className="flex items-center justify-center min-h-[400px]">
					<div className="text-center">
						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
						<p className="text-muted-foreground">Loading check-in...</p>
					</div>
				</div>
			</div>
		);
	}

	if (!checkIn) {
		return (
			<div className="container mx-auto px-4 py-8">
				<Card className="max-w-md mx-auto">
					<CardContent className="text-center py-8">
						<AlertTriangle className="h-12 w-12 mx-auto mb-4 text-destructive" />
						<h3 className="text-lg font-medium mb-2">Check-in Not Found</h3>
						<p className="text-muted-foreground mb-4">
							The check-in you're trying to edit doesn't exist or you don't have
							permission to edit it.
						</p>
						<Button asChild>
							<Link to="/check-ins">Back to Check-ins</Link>
						</Button>
					</CardContent>
				</Card>
			</div>
		);
	}

	return (
		<div className="container mx-auto px-4 py-8">
			{/* Header */}
			<div className="flex items-center justify-between mb-6">
				<div className="flex items-center space-x-4">
					<Button variant="ghost" asChild>
						<Link to="/check-ins" className="flex items-center">
							<ArrowLeft className="h-4 w-4 mr-2" />
							Back to Check-ins
						</Link>
					</Button>
					<div>
						<h1 className="text-2xl font-bold">Edit Check-in</h1>
						<p className="text-muted-foreground flex items-center mt-1">
							<Calendar className="h-4 w-4 mr-1" />
							{formatDate(checkIn.created_at)}
							<Clock className="h-4 w-4 ml-3 mr-1" />
							{new Date(checkIn.created_at).toLocaleTimeString()}
						</p>
					</div>
				</div>
				<div className="flex space-x-2">
					<Button variant="outline" onClick={() => navigate("/check-ins")}>
						Cancel
					</Button>
					<Button
						onClick={handleSave}
						disabled={saving || ![3, 1, -1, -3].includes(editFeeling)}
					>
						{saving ? "Saving..." : "Save Changes"}
					</Button>
				</div>
			</div>

			<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
				{/* General Check-in Section */}
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center">
							<User className="h-5 w-5 mr-2" />
							General Check-in
						</CardTitle>
						<CardDescription>
							Your overall feeling and daily description
						</CardDescription>
					</CardHeader>
					<CardContent className="space-y-4">
						<div>
							<Label className="text-sm font-medium">
								How did you feel compared to your last check-in?
							</Label>
							<Select
								value={editFeeling.toString()}
								onValueChange={(value) => setEditFeeling(Number(value))}
							>
								<SelectTrigger className="mt-2">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="3">
										{getFeelingOptionWithArrows(
											3,
											"Much better than my last daily reflection",
										)}
									</SelectItem>
									<SelectItem value="1">
										{getFeelingOptionWithArrows(
											1,
											"Slightly better than my last daily reflection",
										)}
									</SelectItem>
									<SelectSeparator />
									<SelectItem value="-1">
										{getFeelingOptionWithArrows(
											-1,
											"Slightly worse than my last daily reflection",
										)}
									</SelectItem>
									<SelectItem value="-3">
										{getFeelingOptionWithArrows(
											-3,
											"Much worse than my last daily reflection",
										)}
									</SelectItem>
								</SelectContent>
							</Select>
						</div>

						<div>
							<Label className="text-sm font-medium">
								How would you describe your day?
							</Label>
							<Textarea
								value={editDescription}
								onChange={(e) => setEditDescription(e.target.value)}
								placeholder="Tell us about your day, what happened, how you felt..."
								className="min-h-[120px] mt-2"
							/>
						</div>
					</CardContent>
				</Card>

				{/* Reflection Section */}
				{checkIn.reflection && (
					<Card>
						<CardHeader>
							<CardTitle className="flex items-center">
								<Target className="h-5 w-5 mr-2" />
								Reflection Check-in
							</CardTitle>
							<CardDescription>
								Your thoughts on: {checkIn.reflection.title}
							</CardDescription>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="bg-muted p-3 rounded-lg">
								<h4 className="font-medium text-sm mb-1">Reflection Topic</h4>
								<p className="text-sm">{checkIn.reflection.description}</p>
							</div>

							<div>
								<Label className="text-sm font-medium">
									What is the impact of this reflection?
								</Label>
								<Select
									value={editReflectionFeeling.toString()}
									onValueChange={(value) =>
										setEditReflectionFeeling(Number(value))
									}
								>
									<SelectTrigger className="mt-2">
										<SelectValue placeholder="Select impact level" />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="0">No Impact</SelectItem>
										<SelectSeparator />
										<SelectItem value="3">
											<span className="flex items-center">
												<span className="text-green-600 font-bold mr-1">
													+++
												</span>
												Break-through Positive
											</span>
										</SelectItem>
										<SelectItem value="2">
											<span className="flex items-center">
												<span className="text-green-600 font-bold mr-1">
													++
												</span>
												Highly Positive
											</span>
										</SelectItem>
										<SelectItem value="1">
											<span className="flex items-center">
												<span className="text-green-600 font-bold mr-1">+</span>
												Slightly Positive
											</span>
										</SelectItem>
										<SelectSeparator />
										<SelectItem value="-1">
											<span className="flex items-center">
												<span className="text-red-600 font-bold mr-1">-</span>
												Slightly Negative
											</span>
										</SelectItem>
										<SelectItem value="-2">
											<span className="flex items-center">
												<span className="text-red-600 font-bold mr-1">--</span>
												Highly Negative
											</span>
										</SelectItem>
										<SelectItem value="-3">
											<span className="flex items-center">
												<span className="text-red-600 font-bold mr-1">---</span>
												Critically Negative
											</span>
										</SelectItem>
									</SelectContent>
								</Select>
							</div>

							<div>
								<Label className="text-sm font-medium">
									Describe why the reflection is having this impact
								</Label>
								<Textarea
									value={editReflectionReasons}
									onChange={(e) => setEditReflectionReasons(e.target.value)}
									placeholder="Describe the current impact..."
									className="min-h-[80px] mt-2"
								/>
							</div>

							{/* Signals Section */}
							{checkIn.reflection.signals &&
								checkIn.reflection.signals.length > 0 && (
									<div>
										<Label className="text-sm font-medium block mb-2">
											Did you notice any of your reflection's signals?
										</Label>
										<div className="space-y-2">
											{checkIn.reflection.signals.map((signal: any) => (
												<div
													key={signal.id}
													className="flex items-start space-x-2 p-2 border rounded-lg"
												>
													<input
														type="checkbox"
														id={`signal-${signal.id}`}
														className="mt-1"
														checked={editSignalsNoticed.includes(signal.id)}
														onChange={() => handleSignalToggle(signal.id)}
													/>
													<label
														htmlFor={`signal-${signal.id}`}
														className="text-sm flex-1 cursor-pointer"
													>
														<span className="font-medium">{signal.title}</span>
														{signal.description && (
															<span className="text-muted-foreground block text-xs mt-1">
																{signal.description}
															</span>
														)}
													</label>
													{editSignalsNoticed.includes(signal.id) && (
														<CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
													)}
												</div>
											))}
										</div>
									</div>
								)}
						</CardContent>
					</Card>
				)}

				{/* Current Values Display */}
				<Card className="lg:col-span-2">
					<CardHeader>
						<CardTitle className="flex items-center">
							<FileText className="h-5 w-5 mr-2" />
							Original Check-in Data
						</CardTitle>
						<CardDescription>
							View the original values for reference
						</CardDescription>
					</CardHeader>
					<CardContent>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<div>
								<h4 className="font-medium text-sm mb-2">
									Original General Feeling
								</h4>
								<Badge variant="secondary">
									{getFeelingText(checkIn.general_feeling)}
								</Badge>
							</div>

							{checkIn.reflection && (
								<div>
									<h4 className="font-medium text-sm mb-2">
										Original Reflection Impact
									</h4>
									<Badge variant="secondary">
										{checkIn.reflection_impact ||
											(checkIn.reflection_feeling !== null &&
											checkIn.reflection_feeling !== undefined
												? getImpactText(checkIn.reflection_feeling)
												: "Not set")}
									</Badge>
								</div>
							)}

							<div className="md:col-span-2">
								<h4 className="font-medium text-sm mb-2">
									Original Day Description
								</h4>
								<div className="bg-muted p-3 rounded-lg text-sm">
									{checkIn.day_description || "No description provided"}
								</div>
							</div>

							{checkIn.reflection_reasons && (
								<div className="md:col-span-2">
									<h4 className="font-medium text-sm mb-2">
										Original Reflection Reasons
									</h4>
									<div className="bg-muted p-3 rounded-lg text-sm">
										{checkIn.reflection_reasons}
									</div>
								</div>
							)}

							{checkIn.signals_noticed &&
								checkIn.signals_noticed.length > 0 && (
									<div className="md:col-span-2">
										<h4 className="font-medium text-sm mb-2">
											Originally Noticed Signals
										</h4>
										<div className="flex flex-wrap gap-2">
											{checkIn.signals_noticed.map((signalId, index) => {
												const signal = checkIn.reflection?.signals?.find(
													(s) => s.id === signalId,
												);
												return (
													<Badge key={index} variant="outline">
														{signal?.title || `Signal ${signalId}`}
													</Badge>
												);
											})}
										</div>
									</div>
								)}
						</div>
					</CardContent>
				</Card>
			</div>
		</div>
	);
}
